import {
  ActivityBlock,
  ActivityChange,
  IssueCustomAttribute,
  IssueCustomAttributeDefinition,
} from "@adsk/acc-issues-types";
import { getCustomAttributeDisplayValue } from "../../input-field-components/EditFormCustomField";
import { getCustomFieldEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueCustomFields";
import {
  activityChangeType,
  activityChangeTypes,
  ActivityVerb,
  activityVerbs,
} from "@adsk/acc-issues-types";

const sortChanges =
  (changeTypesToOmit: activityChangeType[] = []) =>
  (activityBlocks: ActivityBlock[]) => {
    return activityBlocks
      .map(activityBlock => {
        const changes: ActivityChange[] = [];

        // sort edit according to activityChangeTypes order
        activityChangeTypes
          .filter(type => !changeTypesToOmit.includes(type))
          .forEach(changeType => {
            const activityChange = activityBlock.changes.find(
              activityChange => activityChange.changeType === changeType
            );
            if (activityChange) {
              changes.push(activityChange);
            }
          });

        if (changes.length === 0) {
          return;
        }

        return {
          ...activityBlock,
          changes,
        };
      })
      .filter(Boolean);
  };

type JSONIssueCustomAttribute = { id: string } & Pick<
  IssueCustomAttribute,
  "title" | "type" | "value"
>;

/**
 * Return all activity log changes for display.
 * This function transforms all existing custom attribute activities of "list" type
 * (which have the custom attribute id as their value), based on the given map ({id => custom attribute}).
 * @param customFieldEntitiesMap
 */
const parseCustomAttributeActivities = (
  customFieldEntitiesMap: ReturnType<typeof getCustomFieldEntitiesMap>
) => {
  return (activityBlocks: ActivityBlock[]) => {
    return activityBlocks.map(activityBlock => {
      const changes = activityBlock.changes.reduce(
        (acc: ActivityChange[], change): ActivityChange[] => {
          if (
            change.changeType !== activityChangeType.ChangedCustomAttributes
          ) {
            acc.push(change);
            return acc;
          }
          const { before, after, verb, object } = change;

          const beforeArray = JSON.parse(before) as [JSONIssueCustomAttribute];
          const afterArray = JSON.parse(after) as [JSONIssueCustomAttribute];
          // Ensure custom attribute changes are well-defined
          const customAttributesLength = Math.min(
            beforeArray.length,
            afterArray.length
          );

          for (let i = 0; i < customAttributesLength; i++) {
            const beforeJson = beforeArray[i];
            const afterJson = afterArray[i];

            const {
              id: attributeDefinitionId,
              title: customAttributeTitle,
              type,
            } = beforeJson;
            const attribute: IssueCustomAttributeDefinition =
              customFieldEntitiesMap[attributeDefinitionId];

            const newActivityChange = {
              verb,
              object,
              changeType: activityChangeType.ChangedCustomAttributes,
              before: getCustomAttributeDisplayValue(
                attribute,
                beforeJson.value
              ),
              after: getCustomAttributeDisplayValue(attribute, afterJson.value),
              customAttributeTitle,
            };

            // TODO:khankid remove UI hack when [SCCOM-7790](https://jira.autodesk.com/browse/SCCOM-7790) fixed
            // BE doesn't save the value of the selected option, but its _id_. In the case the custom field
            // is deleted, we have no option but to reset the values and let downstream decide how to show this.
            if (!attribute && type === "list") {
              newActivityChange.after = "";
              newActivityChange.before = "";
            }
            acc.push(newActivityChange);
          }

          return acc;
        },
        []
      );

      return {
        ...activityBlock,
        changes,
      };
    });
  };
};

const parseReferenceChange = (value: string) => {
  if (!value) {
    return "";
  }
  const { id, domain, type } = JSON.parse(value);
  return {
    entityId: id,
    entityDomain: domain,
    entityType: type,
  };
};

/**
 * Reference changes are encoded into before/after as a JSON,
 * just like custom attributes.
 */
const parseReferenceChanges = (activityBlocks: ActivityBlock[]) => {
  return activityBlocks.map(activityBlock => {
    const changes: ActivityChange[] = activityBlock.changes.map(change => {
      if (change.changeType !== activityChangeType.ChangedReference) {
        return change;
      }

      return {
        ...change,
        after: parseReferenceChange(change.after),
        before: parseReferenceChange(change.before),
      };
    });

    return {
      ...activityBlock,
      changes,
    };
  });
};

const flattenEditVerb = (activityBlocks: ActivityBlock[]) => {
  const getFlattenedVerb = (oldVerb: ActivityVerb) => {
    const issueEditVerbs: ActivityVerb[] = [
      activityVerbs.ISSUE_OPEN,
      activityVerbs.ISSUE_CLOSED,
      activityVerbs.ISSUE_PENDING,
      activityVerbs.ISSUE_IN_REVIEW,
      activityVerbs.ISSUE_DUE_DATE,
      activityVerbs.ISSUE_ASSIGN,
    ];
    if (issueEditVerbs.includes(oldVerb)) {
      return activityVerbs.ISSUE_EDIT;
    }
    return oldVerb;
  };

  return activityBlocks.map(activityBlock => ({
    ...activityBlock,
    verb: getFlattenedVerb(activityBlock.verb),
  }));
};

const removeRedundantChanges = (activityBlocks: ActivityBlock[]) => {
  return activityBlocks.map(activityBlock => {
    if (activityBlock.verb === activityVerbs.ISSUE_EDIT) {
      return {
        ...activityBlock,
        // When deleting a 3D placement, we do not display snapshot changes
        changes: activityBlock.changes.filter(
          (change: ActivityChange) =>
            change.changeType !== activityChangeType.ChangedSnapshot
        ),
      };
    }

    return activityBlock;
  });
};

const splitEditVerb = (originalactivityBlocks: ActivityBlock[]) => {
  const transformedActivityBlocks: ActivityBlock[] = [];

  originalactivityBlocks.forEach(activityBlock => {
    if (activityBlock.verb === activityVerbs.ISSUE_EDIT) {
      activityBlock.changes.forEach(activityChange => {
        transformedActivityBlocks.push({
          ...activityBlock,
          changes: [activityChange],
        });
      });
    } else {
      transformedActivityBlocks.push(activityBlock);
    }
  });

  return transformedActivityBlocks;
};

// eslint-disable-next-line @typescript-eslint/ban-types
const pipe = (...functionOperators: Function[]) => {
  return <T>(arg: T) =>
    functionOperators.reduce((pipedValue, func) => func(pipedValue), arg);
};

export const parseActivitiesData = (
  activityBlocks: ActivityBlock[],
  customFieldEntitiesMap: ReturnType<typeof getCustomFieldEntitiesMap>,
  changeTypesToOmit: activityChangeType[]
) => {
  return pipe(
    flattenEditVerb,
    removeRedundantChanges,
    splitEditVerb,
    sortChanges(changeTypesToOmit),
    parseCustomAttributeActivities(customFieldEntitiesMap),
    parseReferenceChanges
  )(activityBlocks);
};
